import React from 'react'
import { Button, ButtonStyle } from '../../components/simple/button'
import { SpecialPanel } from '../../layouts/components/panel'
import { SimpleLayout } from '../../layouts/simple'
import { DateInput, TextInput } from '../../components/form/textInput'
import { Select } from '../../components/form/select'
import { stringToDate } from '../../components/simple/date'
import { Teacher, useAppContext } from '../../providers/context'
import { laravelValidationToToast, makeRequest, showAxiosErrorAsToast, useRequest } from '../../request'
import { useNavigate } from 'react-router-dom'
import { Toasts } from '../../providers/toast'
import { FloatingLoader } from '../../components/simple/loader'
import { Popups } from '../../providers/popups'
import { deltaToText, QuillTextarea } from '../../components/quill/quillTextarea'
import { prepareDeltaToPost, stringToDelta } from '../../utils'
import Delta from 'quill-delta'

// Simple Student Selector Component
const StudentSelector: React.FC<{
  userID: number
  selectedStudentID?: number
  onStudentSelect: (studentID: number | undefined) => void
}> = ({ userID, selectedStudentID, onStudentSelect }) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [selectedStudentName, setSelectedStudentName] = React.useState('')
  const [students, setStudents] = React.useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = React.useState(false)

  const searchStudents = React.useCallback(
    async (term: string) => {
      if (term.length < 2) {
        setStudents([])
        return
      }

      setLoading(true)
      try {
        const result = await makeRequest<{ id: number; name: string }[]>('/frontend_api/searchStudents', {
          search: term,
          userID,
        })
        setStudents(result.map((s) => ({ id: s.id, name: s.name })))
      } catch (error) {
        console.error('Failed to search students:', error)
      } finally {
        setLoading(false)
      }
    },
    [userID]
  )

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      searchStudents(searchTerm)
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchTerm, searchStudents])

  const selectedStudent = students.find((s) => s.id === selectedStudentID)

  return (
    <div className="relative">
      <TextInput
        value={selectedStudentID ? selectedStudentName : searchTerm}
        onChange={(value) => {
          if (selectedStudentID) {
            // If there's a selected student and user types, clear selection and start new search
            onStudentSelect(undefined)
            setSelectedStudentName('')
            setSearchTerm(value)
          } else {
            // Normal search mode
            setSearchTerm(value)
            if (!value) onStudentSelect(undefined)
          }
        }}
        placeholder="Type student name to search..."
      />
      {selectedStudentID && (
        <button
          className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
          onClick={() => {
            onStudentSelect(undefined)
            setSelectedStudentName('')
            setSearchTerm('')
          }}
        >
          ✕
        </button>
      )}
      {loading && <div className="absolute right-2 top-2 text-sm text-gray-500">Searching...</div>}
      {students.length > 0 && !selectedStudentID && searchTerm && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-lg">
          {students.map((student) => (
            <div
              key={student.id}
              className="cursor-pointer px-3 py-2 hover:bg-gray-100"
              onClick={() => {
                onStudentSelect(student.id)
                setSelectedStudentName(student.name)
                setSearchTerm('')
              }}
            >
              {student.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

type AssessmentEditData = {
  id: number
  name: string
  introNote: string
  expirationDate: string
  security: (typeof securityOptions)[number]['value']
  published: boolean
  hasBeenSent: boolean
  processing: boolean
  schoolID: string
  oneStudent: boolean
  studentID?: number
}

const securityOptions = [
  { value: 'last_name', name: 'Last Name' },
  { value: 'birthdate', name: 'Birthdate' },
  { value: 'student_number', name: 'Student Number' },
]

const CreateOrEditAssessment: React.FC<{
  user: Teacher
  assessment?: AssessmentEditData
  setPublished?: (published: boolean) => Promise<void>
}> = ({ user, assessment, setPublished }) => {
  const editing = !!assessment
  const [assessmentName, setAssessmentName] = React.useState(assessment?.name || '')
  const [note, setNote] = React.useState(() =>
    assessment?.introNote
      ? stringToDelta(assessment.introNote)
      : new Delta([
          {
            insert:
              'Please click the link to see your document. Please contact your teachers directly if you have any questions or concerns.',
          },
        ])
  )
  const [HTMLNote, setHTMLNote] = React.useState<string | undefined>(undefined)
  const [expirationDate, setExpirationDate] = React.useState(
    assessment?.expirationDate ? stringToDate(assessment.expirationDate) : undefined
  )
  const [security, setSecurity] = React.useState(
    securityOptions.find((s) => s.value == (assessment?.security ?? '')) || securityOptions[0]
  )

  // Recipients options - now includes "One Student" option
  const recipientOptions = React.useMemo(() => {
    const options = [
      { name: 'Select...', value: '', initialOnly: true },
      { name: 'One Student', value: 'one_student' },
    ]
    if (user.districtAccess) {
      options.push({ name: 'District', value: 'district' })
    }
    options.push(...user.district.schools.map((s) => ({ name: s.name, value: String(s.id) })))
    return options
  }, [user])

  const [recipient, setRecipient] = React.useState<{ name: string; value: string } | undefined>(() => {
    if (assessment?.oneStudent) {
      return recipientOptions.find((o) => o.value === 'one_student')
    }
    if (assessment?.schoolID) {
      return recipientOptions.find((s) => s.value === String(assessment.schoolID))
    }
    if (user.districtAccess && !assessment?.schoolID && assessment) {
      return recipientOptions.find((o) => o.value === 'district')
    }
    return undefined // Default to no selection for new assessments
  })

  const [selectedStudentID, setSelectedStudentID] = React.useState<number | undefined>(assessment?.studentID)

  const [loading, setLoading] = React.useState(false)

  const navigate = useNavigate()

  React.useEffect(() => {
    if (assessment?.processing) navigate(`/assessment/view/${assessment.id}`)
  }, [assessment?.processing])

  const save = async () => {
    // Validation: Require recipient selection
    if (!recipient || recipient.value === '') {
      Toasts.showToast({ message: 'Please select recipients before saving.', error: true })
      return
    }

    const isOneStudent = recipient?.value === 'one_student'

    // Validation: If "One Student" is selected, require student selection
    if (isOneStudent && !selectedStudentID) {
      Toasts.showToast({ message: 'Please select a student when sending to one student.', error: true })
      return
    }

    setLoading(true)
    try {
      const requestData = {
        name: assessmentName,
        intro_note: prepareDeltaToPost(note),
        expiration_date: expirationDate,
        security: security.value,
        school: isOneStudent ? '' : recipient?.value === 'district' ? '' : recipient?.value || '',
        oneStudent: isOneStudent ? 1 : 0,
        student_id: isOneStudent ? selectedStudentID : undefined,
        id: assessment?.id,
      }

      console.log('Frontend sending data:', requestData)

      const id = await makeRequest<number>('/frontend_api/updateAssessment', requestData)
      navigate(`/assessment/view/${id}`)
    } catch (error) {
      laravelValidationToToast('Failed to save this document', error)
    } finally {
      setLoading(false)
    }
  }

  const deleteAssessment = async () => {
    if (!(await Popups.simpleConfirm('Are you sure you want to completely delete this document?'))) return
    setLoading(true)
    try {
      await makeRequest('/frontend_api/deleteAssessment', { assessmentID: assessment?.id })
      navigate('/')
    } catch (error) {
      showAxiosErrorAsToast(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <SimpleLayout verticalScroll>
      <SpecialPanel className={`${loading ? 'pointer-events-none opacity-50' : ''} mb-8 mt-8 sm:!w-[650px]`}>
        {loading && <FloatingLoader />}
        <div className="flex justify-between self-stretch">
          <div className="text-2xl font-semibold">
            {editing ? 'Edit Document Delivery' : 'Create A Document Delivery'}
          </div>
          {assessment && (
            <div className="flex gap-2">
              <Button link={`/assessment/view/${assessment.id}`}>Cancel</Button>
              {setPublished && (
                <Button
                  buttonStyle={ButtonStyle.warning}
                  onClick={async () => {
                    setLoading(true)
                    await setPublished(!assessment.published)
                    setLoading(false)
                  }}
                >
                  {assessment.published ? 'Unpublish' : 'Publish'}
                </Button>
              )}
            </div>
          )}
        </div>
        <a
          href="https://reachmyteach.zendesk.com/hc/en-us/articles/46917076771609-Document-Delivery"
          className="hover:underline‰ -mt-2 text-link no-underline"
          target="_blank"
        >
          See Guide Here
        </a>

        <div className="mt-2">Document Name</div>
        <TextInput value={assessmentName} onChange={setAssessmentName} />
        <div className="mt-2">Recipients</div>
        <Select
          options={recipientOptions}
          value={recipient || recipientOptions[0]}
          onChange={(value) => setRecipient(value as { name: string; value: string })}
          disabled={editing}
        />

        {recipient?.value === 'one_student' && (
          <>
            <div className="mt-2">Select Student</div>
            <StudentSelector
              userID={user.id}
              selectedStudentID={selectedStudentID}
              onStudentSelect={setSelectedStudentID}
            />
          </>
        )}

        <div className="mt-2">Introduction Note</div>
        <div className="flex max-h-96 self-stretch">
          <QuillTextarea
            className="rounded-lg"
            formats={['bold', 'italic', 'list', 'link']}
            valueDelta={note}
            valueHTML={HTMLNote}
            onValueChange={(h, d) => {
              setHTMLNote(h)
              setNote(d)
            }}
          />
        </div>
        <div className="-mt-1 text-sm">
          This information will appear to families before they view the document. It can be as long as you like and will
          be translated, if needed.
        </div>
        <div className="mt-2">Expiration Date (optional)</div>
        <DateInput value={expirationDate} onChange={setExpirationDate} />
        <div className="mt-2">Security</div>
        <Select options={securityOptions} value={security} onChange={setSecurity} disabled={assessment?.hasBeenSent} />
        <div className="mt-3 flex justify-between self-stretch">
          <Button
            logo
            onClick={save}
            disabled={
              !assessmentName.trim() ||
              !deltaToText(note).trim() ||
              (recipient?.value === 'one_student' && !selectedStudentID)
            }
          >
            {editing ? 'Update' : 'Create'}
          </Button>
          {editing && (
            <Button buttonStyle={ButtonStyle.ghostRed} onClick={deleteAssessment}>
              {'Delete All'}
            </Button>
          )}
        </div>
      </SpecialPanel>
    </SimpleLayout>
  )
}

export const CreateAssessment: React.FC<{ user: Teacher }> = ({ user }) => <CreateOrEditAssessment user={user} />
export const EditAssessment: React.FC<{ id: number; user: Teacher }> = ({ id, user }) => {
  const {
    data: assessment,
    loading,
    reload,
  } = useRequest<AssessmentEditData>(
    `/frontend_api/getAssessmentEditData`,
    React.useMemo(() => ({ id }), [id])
  )

  const { startLoading, stopLoading } = useAppContext()
  React.useEffect(() => {
    if (loading) startLoading({ key: 'assessmentEditData', showLoaderInstantly: true })
    else stopLoading('assessmentEditData')
  }, [loading])

  if (!assessment) return null
  return (
    <CreateOrEditAssessment
      user={user}
      assessment={assessment}
      setPublished={async (p) => {
        if (p && !(await Popups.simpleConfirm('Are you sure you want to publish this assessment?'))) return
        if (!p && !(await Popups.simpleConfirm('Are you sure you want to unpublish this assessment?'))) return
        try {
          await makeRequest(`/frontend_api/setAssessmentPublished`, {
            id: assessment.id,
            published: p,
          })
        } catch (error) {
          showAxiosErrorAsToast(error)
        }
        await reload()
      }}
    />
  )
}
